import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from 'constants/routes';
import { useCoupons } from 'hooks/useFetchCoupons';
import { useUserStore } from 'stores/user';
import { AppointmentType, type Center, FLOW_MODES } from 'types/appointments';
import { mapBookedContactMethod } from 'utils/appointments';
import { useFetchPsychologistHistoricalPatients } from './useFetchPsychologistHistoricalPatients';

export const useBookSession = (
  patientId: string
): {
  center: Center | undefined;
  isLoading: boolean;
  handleBookAppointment: () => void;
} => {
  const { data: patients, isLoading: isLoadingPatients } = useFetchPsychologistHistoricalPatients();
  const patientData = patients?.find(patient => patient.patient.id === patientId);
  const patient = patientData?.patient;
  const centerId = patientData?.centerId;
  const specialitiesBookable = patientData?.specialitiesBookable;
  const psychologist = useUserStore(state => state.user);
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.PSYCHOLOGIST);

  const {
    remainingSessions,
    remainingSessionsThisMonth,
    hasCopaid,
    isLoading: isLoadingCoupons,
  } = useCoupons(patient?.id, specialitiesBookable, !!patient?.id);

  const hasSessions = remainingSessions > 0 || (!hasCopaid && remainingSessionsThisMonth > 0);
  const bookedContactMethod = mapBookedContactMethod(AppointmentType.InPerson, centerId);
  const isLoading = isLoadingCoupons || isLoadingPatients;

  const handleBookAppointment = (): void => {
    if (!hasSessions) {
      toast.error(t('PsychologistPatients.empty'), { id: 'no-sessions-error' });
      return;
    }
    const state = {
      patientId,
      appointment: {
        specialitiesBookable,
        center: patientData?.center,
        bookedContactMethod,
      },
      psychologist,
      flow: FLOW_MODES.recent_psychologist,
    };
    navigate(ROUTES.APPOINTMENTS_RESERVE, { state });
  };

  return {
    center: patientData?.center,
    isLoading,
    handleBookAppointment,
  };
};
